import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar as CalendarIcon, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { format, subYears } from "date-fns";
import api from "@/lib/api";

const TIME_RANGES = [
  { value: "1M", label: "1 Month" },
  { value: "6M", label: "6 Months" },
  { value: "1Y", label: "1 Year" },
  { value: "2Y", label: "2 Years" },
  { value: "5Y", label: "5 Years" },
];

const PERIOD_YEARS = { "1M": 1/12, "6M": 0.5, "1Y": 1, "2Y": 2, "5Y": 5, "10Y": 10 };

const Performance = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [performance, setPerformance] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [timeRange, setTimeRange] = useState("1Y");
  const [startDate, setStartDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const fetchPortfolio = useCallback(async () => {
    try {
      const data = await api.getPortfolio(id);
      setPortfolio(data);
    } catch (error) {
      toast.error("Failed to load portfolio");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchPerformance = useCallback(async () => {
    if (!portfolio?.assets?.length) {
      setPerformance(null);
      return;
    }
    setPerformanceLoading(true);
    try {
      const data = await api.getPortfolioPerformance(
        id, 
        timeRange, 
        startDate ? format(startDate, 'yyyy-MM-dd') : null
      );
      setPerformance(data);
    } catch (error) {
      console.error("Failed to load performance:", error);
      toast.error("Failed to load performance data");
    } finally {
      setPerformanceLoading(false);
    }
  }, [id, portfolio?.assets?.length, timeRange, startDate]);

  useEffect(() => {
    fetchPortfolio();
  }, [fetchPortfolio]);

  useEffect(() => {
    if (portfolio) {
      fetchPerformance();
    }
  }, [portfolio, timeRange, startDate, fetchPerformance]);

  const handleDateSelect = (date) => {
    setStartDate(date);
    setCalendarOpen(false);
  };

  const clearStartDate = () => {
    setStartDate(null);
  };

  const homeCurrency = performance?.home_currency || portfolio?.home_currency || "USD";

  const fmtNum = (num, decimals = 2) => {
    if (num === null || num === undefined || isNaN(num)) return '0.00';
    const parts = Number(num).toFixed(decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'");
    return parts.join('.');
  };

  const formatCurrency = (value) => {
    return `${currencySymbol === "CHF" ? "CHF " : currencySymbol}${fmtNum(value)}`;
  };

  const currencySymbol = homeCurrency === "EUR" ? "€" : homeCurrency === "CHF" ? "CHF" : "$";

  const chartData = performance?.dates?.map((date, idx) => ({
    date,
    value: performance.values[idx],
  })) || [];

  // Calculate min date for calendar (5 years ago)
  const minDate = subYears(new Date(), 5);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F766E]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="glass-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              data-testid="back-to-portfolio-btn"
              onClick={() => navigate(`/portfolio/${id}`)}
              className="hover:bg-slate-100"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Performance Analysis
              </h1>
              <p className="text-slate-500 text-sm">{portfolio?.name}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Controls */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          {/* Time Range Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 mr-2">Period:</span>
            {TIME_RANGES.map((range) => (
              <button
                key={range.value}
                data-testid={`perf-time-range-${range.value}`}
                onClick={() => {
                  setTimeRange(range.value);
                  setStartDate(null);
                }}
                className={`time-range-btn ${timeRange === range.value && !startDate ? 'active' : ''}`}
              >
                {range.label}
              </button>
            ))}
          </div>

          {/* Custom Start Date */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">Custom start:</span>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="select-start-date-btn"
                  className={`justify-start text-left font-normal ${startDate ? 'text-slate-900' : 'text-slate-500'}`}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={handleDateSelect}
                  disabled={(date) => date > new Date() || date < minDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {startDate && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={clearStartDate}
                className="text-slate-500 hover:text-slate-700"
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Performance Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-slate-200 shadow-none">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500 mb-1">Starting Value</p>
              <p className="text-2xl font-semibold" style={{ fontFamily: 'Manrope' }}>
                {performance?.values?.length > 0 ? formatCurrency(performance.values[0]) : '$0.00'}
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-none">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500 mb-1">Current Value</p>
              <p className="text-2xl font-semibold" style={{ fontFamily: 'Manrope' }}>
                {performance ? formatCurrency(performance.total_value) : '$0.00'}
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-none">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500 mb-1">Absolute Change</p>
              <p className={`text-2xl font-semibold flex items-center ${
                performance?.absolute_change >= 0 ? 'text-emerald-600' : 'text-red-500'
              }`} style={{ fontFamily: 'Manrope' }}>
                {performance?.absolute_change >= 0 ? (
                  <TrendingUp className="w-5 h-5 mr-2" />
                ) : (
                  <TrendingDown className="w-5 h-5 mr-2" />
                )}
                {performance ? formatCurrency(Math.abs(performance.absolute_change)) : '$0.00'}
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-none">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500 mb-1">Percent Change</p>
              <p className={`text-2xl font-semibold flex items-center ${
                performance?.percent_change >= 0 ? 'text-emerald-600' : 'text-red-500'
              }`} style={{ fontFamily: 'Manrope' }}>
                {performance?.percent_change >= 0 ? (
                  <TrendingUp className="w-5 h-5 mr-2" />
                ) : (
                  <TrendingDown className="w-5 h-5 mr-2" />
                )}
                {performance ? `${performance.percent_change >= 0 ? '+' : ''}${performance.percent_change}%` : '0%'}
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-none">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500 mb-1">Annualized Return</p>
              {(() => {
                if (!performance || !performance.start_value || performance.start_value <= 0) return <p className="text-2xl font-semibold" style={{ fontFamily: 'Manrope' }}>-</p>;
                let years;
                if (startDate) {
                  const msPerYear = 365.25 * 24 * 60 * 60 * 1000;
                  years = (new Date() - new Date(startDate)) / msPerYear;
                } else {
                  years = PERIOD_YEARS[timeRange] || 1;
                }
                if (years <= 0) return <p className="text-2xl font-semibold" style={{ fontFamily: 'Manrope' }}>-</p>;
                const annualized = (Math.pow(performance.total_value / performance.start_value, 1 / years) - 1) * 100;
                return (
                  <p className={`text-2xl font-semibold flex items-center ${
                    annualized >= 0 ? 'text-emerald-600' : 'text-red-500'
                  }`} style={{ fontFamily: 'Manrope' }} data-testid="perf-annualized-return">
                    {annualized >= 0 ? (
                      <TrendingUp className="w-5 h-5 mr-2" />
                    ) : (
                      <TrendingDown className="w-5 h-5 mr-2" />
                    )}
                    {annualized >= 0 ? '+' : ''}{annualized.toFixed(2)}% p.a.
                  </p>
                );
              })()}
            </CardContent>
          </Card>
        </div>

        {/* Main Chart */}
        <Card className="border-slate-200 shadow-none">
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope' }}>
              Portfolio Value Over Time
              {startDate && (
                <span className="text-sm font-normal text-slate-500 ml-2">
                  (from {format(startDate, 'MMM d, yyyy')})
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {performanceLoading ? (
              <div className="h-96 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#0F766E]" />
              </div>
            ) : chartData.length > 0 ? (
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorValuePerf" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2DD4BF" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#2DD4BF" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="date" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#64748B' }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        const mm = String(date.getMonth() + 1).padStart(2, '0');
                        const yy = String(date.getFullYear()).slice(-2);
                        return `${mm}-${yy}`;
                      }}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#64748B' }}
                      tickFormatter={(value) => {
                        const k = Math.round(value / 1000);
                        return `${currencySymbol}${String(k).replace(/\B(?=(\d{3})+(?!\d))/g, "'")}k`;
                      }}
                      width={70}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px',
                        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)'
                      }}
                      formatter={(value) => [formatCurrency(value), 'Portfolio Value']}
                      labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', { 
                        weekday: 'long',
                        month: 'long', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    />
                    {chartData.length > 0 && (
                      <ReferenceLine 
                        y={chartData[0].value} 
                        stroke="#94A3B8" 
                        strokeDasharray="3 3"
                        label={{ value: 'Start', fill: '#64748B', fontSize: 12 }}
                      />
                    )}
                    <Area 
                      type="linear" 
                      dataKey="value" 
                      stroke="#0F766E" 
                      strokeWidth={2.5}
                      fill="url(#colorValuePerf)" 
                      animationDuration={1000}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-96 flex flex-col items-center justify-center text-slate-400">
                <CalendarIcon className="w-16 h-16 mb-4 opacity-50" />
                <p className="text-lg mb-2">No performance data available</p>
                <p className="text-sm">Add assets to your portfolio to track performance</p>
                <Button 
                  variant="link" 
                  className="text-[#0F766E] mt-4"
                  onClick={() => navigate(`/portfolio/${id}`)}
                >
                  Go to portfolio
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Holdings Summary */}
        {portfolio?.assets?.length > 0 && (
          <Card className="border-slate-200 shadow-none mt-6">
            <CardHeader>
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope' }}>
                Current Holdings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {portfolio.assets.map((asset) => (
                  <div 
                    key={asset.symbol}
                    className="p-4 bg-slate-50 rounded-xl"
                  >
                    <p className="font-semibold text-slate-900">{asset.symbol}</p>
                    <p className="text-sm text-slate-500">{asset.quantity} units</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default Performance;
